import { Button, Checkbox, Col, Flex, Form, Input, List, Modal, Popover, Row, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { FaCircleInfo } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isSuperAdmin } from '../../helper';
import { addRestaurant, deleteRestaurant, updateRestaurant } from '../../redux/actions/restaurant';
import BusinessHours from '../BusinessHours';

const { confirm } = Modal;
const { TextArea } = Input;

const AddShop = ({ title, open, onClose, data }) => {
  const navigate = useNavigate()
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [openingHours, setOpeningHours] = useState(data?.opening_times ?? []);

  const currentDay = dayjs().format('dddd');

  const handleFinish = async (values) => {
    const body = {
      ...values,
      allow_staff_create_task: values.allow_staff_create_task ? 1 : 0,
      opening_hours: openingHours,
    };
    const res = data ? await updateRestaurant({
      ...body,
      id: data.id,
    }) : await addRestaurant(body);
    if (res?.ack) onClose();
  };

  const handleDelete = () => {
    confirm({
      title: 'Are you sure you want to delete this site?',
      content: 'All associated tasks including pending, completed, scheduled, and archived will also be permanently deleted. This action cannot be undone.',
      centered: true,
      okText: 'Yes',
      styles: {
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: '#ffffff80'
        }
      },
      okButtonProps: {
        danger: true
      },
      onOk() {
        return new Promise(async (resolve, reject) => {
          const res = await deleteRestaurant({ id: data?.id });
          if (res?.ack) {
            resolve();
            navigate('/')
          }
        });
      },
    });
  }

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        allow_staff_create_task: data.allow_staff_create_task === 1 || data.allow_staff_create_task === true,
      });
    }
  }, [form, data]);

  return (
    <Fragment>
      <Modal
        title={title ?? 'Register Site'}
        open={open}
        onOk={() => form.submit()}
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: '#00000015',
          },
          content: {
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          },
        }}
        onCancel={onClose}
        footer={[
          data && (isSuperAdmin() || isAdmin()) ? (
            <Button
              key="delete"
              danger
              onClick={handleDelete}
            >
              Delete
            </Button>
          ) : (
            <Button key="cancel" onClick={onClose}>
              Cancel
            </Button>
          ),
          <Button key="ok" type="primary" onClick={() => form.submit()}>
            {data ? "Update" : "Register"}
          </Button>,
        ]}
        width={800}
        centered
        wrapClassName="create-task-modal"
      >

        <Form form={form} layout="vertical" size="large" onFinish={handleFinish}>
          <Row gutter={24}>
            <Col span={12}>
              {/* Site Name */}
              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: 'Please enter the site title!' }]}
              >
                <Input placeholder="Enter site title" />
              </Form.Item>
            </Col>

            {/* Contact Email */}
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, type: 'email', message: 'Please enter a valid email address!' },
                ]}
              >
                <Input placeholder="admin@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              {/* Phone Number */}
              <Form.Item
                label="Phone Number"
                name="phone"
                rules={[{ required: true, message: 'Please enter the phone number!' }]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              {/* Postcode */}
              <Form.Item
                label="Postcode"
                name="postcode"
                rules={[{ required: true, message: 'Please enter the postcode!' }]}
              >
                <Input placeholder="Enter postcode" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              {/* Address */}
              <Form.Item
                label="Address"
                name="address"
                rules={[{ required: true, message: 'Please enter the address!' }]}
              >
                <TextArea placeholder="Enter site address" rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              {/* Opening Hours */}
              <Form.Item label="  ">
                <Flex align="center" justify="space-between">
                  <Button onClick={() => setIsOpen(true)}>Opening Hours</Button>
                  <Popover
                    trigger="click"
                    content={
                      <List
                        bordered
                        dataSource={openingHours}
                        style={{ minWidth: 230, maxHeight: 300, overflowY: 'auto' }}
                        locale={{
                          emptyText: 'Select opening hours to preview.'
                        }}
                        renderItem={(item) => (
                          <List.Item
                            key={item.day}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: item.day === currentDay && '#e6f7ff',
                            }}
                          >
                            <Flex align="center" justify="space-between" style={{ width: '100%' }}>
                              <Typography.Text strong>{item.day}</Typography.Text>
                              {item.enabled ? (
                                <Typography.Text>
                                  {item.openTime} - {item.closeTime}
                                </Typography.Text>
                              ) : (
                                <Typography.Text type="secondary">Closed</Typography.Text>
                              )}
                            </Flex>
                          </List.Item>
                        )}
                      />
                    }
                  >
                    <Button shape="circle" color="primary" variant="filled">
                      <FaCircleInfo />
                    </Button>
                  </Popover>
                </Flex>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <div className="permission-setting-card">
                <div>
                  <Typography.Text strong className="setting-title">
                    Allow Staff to Create Tasks
                  </Typography.Text>
                  <div className="setting-description">
                    Allow staff members to create tasks for this site.
                  </div>
                </div>
                <Form.Item
                  name="allow_staff_create_task"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      {isOpen && (
        <BusinessHours
          open={isOpen}
          onClose={() => setIsOpen(false)}
          data={openingHours}
          onSubmit={(data) => setOpeningHours(data)}
        />
      )}
    </Fragment>
  );
};

export default AddShop;
